import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { NYDUS_NETWORK_API_BASE_URL } from '@rd/core/tokens/rd-lib.token';
import { Offer, OfferCategoryOption, OfferChannelOption } from '@rd/types';
import * as CSS from 'csstype';
import { OfferSettingService } from '../services/offer-setting.service';

@Injectable()
export class OfferHelper {
  constructor(
    @Inject(NYDUS_NETWORK_API_BASE_URL)
    protected nydusNetworkApiBaseUrl: string,
    private translateService: TranslateService,
    private offerSettingService: OfferSettingService,
    private urlUtilsService: UrlUtilsService,
  ) {}

  listingPageUrl(): string {
    return '/offers';
  }

  detailsPageUrl(id: string): string {
    return `/offers/details/${id}`;
  }

  imageRatioClass(): string {
    switch (this.offerSettingService.offerImageRatio) {
      case '1:1':
        return 'one-to-one';
      case '4:2':
        return 'four-to-two';
      case '4:3':
      default:
        return 'four-to-three';
    }
  }

  categoryOptionAriaLabel(filterOption: OfferCategoryOption): string {
    if (filterOption.value === '') {
      return this.translateService.instant(
        'offer.filter_modal.aria.category.all',
      );
    }

    return this.translateService.instant('offer.filter_modal.aria.filter_by', {
      filterOption: this.translateService.instant(filterOption.name),
    });
  }

  channelOptionAriaLabel(filterOption: OfferChannelOption): string {
    if (filterOption.value === '') {
      return this.translateService.instant(
        'offer.filter_modal.aria.channel.all',
      );
    }

    return this.translateService.instant('offer.filter_modal.aria.filter_by', {
      filterOption: this.translateService.instant(filterOption.name),
    });
  }

  ctaRedirectParams({
    offer,
    idToken,
    extraParams,
  }: {
    offer: Offer;
    idToken: string;
    extraParams?: Record<string, string | null | undefined>;
  }): string {
    const params = new URLSearchParams();

    if (idToken) {
      params.set('id_token', idToken);
    }

    Object.entries(extraParams ?? {}).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });

    const queryString = params.toString();
    return `${this.nydusNetworkApiBaseUrl}/offers/${offer.id}/redirect${
      queryString ? `?${queryString}` : ''
    }`;
  }

  coverImage(offer: Offer): CSS.Properties {
    const backgroundImageUrl =
      offer.coverImageUrl ||
      this.urlUtilsService.imageUrl('/images/OH_OfferImage.jpg');

    return {
      backgroundImage: `url(${backgroundImageUrl}?width=1000)`,
    };
  }

  hasMultipleLocations(offer: Offer): boolean {
    return offer.locations && offer.locations.length > 1;
  }
}
